import { AirPath } from "@domain/project/floorplan/air-path";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { Wire } from "@domain/project/floorplan/wiring/wire";
import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone } from "@domain/project/floorplan/zones/ex-zone";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";
import { WildcardZone } from "@domain/project/floorplan/zones/wildcard-zone";

export enum LabelSide {
  RIGHT = "right",
  LEFT = "left",
  TOP = "top",
  BOTTOM = "bottom",
}
export const LABEL_PLACEMENT_DEFAULT = {
  side: LabelSide.RIGHT,
  gap: 7,
};

export enum LabelPlacementMode {
  FIXED = "fixed",
  ROTATING = "rotating",
}

export type FixedLabelPlacement = {
  side: LabelSide;
  gap: number;
  offsetX?: number;
  offsetY?: number;
};

export type RotatingLabelPlacement = {
  side: LabelSide;
  gap: number;
};

export type LabelPlacementProfile =
  | { mode: LabelPlacementMode.ROTATING; placement: RotatingLabelPlacement }
  | { mode: LabelPlacementMode.FIXED; placement: FixedLabelPlacement };

export class LabelPlacementResolver {
  static resolveRotatingSide(): LabelSide {
    return LabelSide.RIGHT;
  }

  static resolveMode(item: FloorplanItem): LabelPlacementMode {
    if (
      item instanceof AirPath ||
      item instanceof CableRoute ||
      item instanceof MeasurementLine ||
      item instanceof ReferenceMeasure ||
      item instanceof Pipeline ||
      item instanceof ProtectiveTube ||
      item instanceof Wire ||
      item instanceof DangerArea ||
      item instanceof DustExZone ||
      item instanceof ExZone ||
      item instanceof WildcardZone ||
      item instanceof StandardRoom ||
      item instanceof IsolationRoom ||
      item instanceof ProcedureRoom ||
      item instanceof ItRoom ||
      item instanceof OperatingTheatre
    ) {
      return LabelPlacementMode.ROTATING;
    }
    if (
      item instanceof FloorplanProductItem ||
      item instanceof FloorplanPlaceholder ||
      item instanceof FloorplanText ||
      item instanceof FloorplanImage
    ) {
      return LabelPlacementMode.FIXED;
    }
    return LabelPlacementMode.FIXED;
  }

  static resolveProfile(item: FloorplanItem): LabelPlacementProfile {
    const mode = this.resolveMode(item);
    const fixedProfile = LABEL_PLACEMENT_DEFAULT;
    const gap = fixedProfile.gap;

    if (mode === LabelPlacementMode.ROTATING) {
      return { mode, placement: { side: this.resolveRotatingSide(), gap } };
    }

    return { mode, placement: { ...fixedProfile } };
  }
}
